/*
 * @Author: Ryan
 * @Date: 2026-02-02
 * @Description: Real-world use cases section with accordion-style tabs and image display
 */
import Image from 'next/image';
import React, { useMemo, useState } from 'react';

import { useImagePreload } from '@/hooks/useImagePreload';

interface UseCase {
  id: string;
  title: string;
  description: string;
  imageUrl: string;
}

const useCases: UseCase[] = [
  {
    id: 'ecommerce',
    title: 'Ecommerce',
    description:
      'Product packaging, promotional banners, and catalog images can be translated instantly while preserving design integrity. Customers worldwide see clear, accurate descriptions in their own language, building trust and boosting conversions. Expanding into new markets is easier with AI image translator.',
    imageUrl:
      'https://assets.falcocut.ai/ai-image-translator/use-case-ecommerce-image-translation.webp',
  },
  {
    id: 'food-service',
    title: 'Food Service',
    description:
      'Menus and promotional images can be translated for international diners with ease. FalcoCut keeps dish names, ingredients, and descriptions clear while preserving design. Restaurants, cafes, and food businesses can serve diverse customers confidently, ensuring visitors understand offerings.',
    imageUrl:
      'https://assets.falcocut.ai/ai-image-translator/use-case-food-service-menu-translation.webp',
  },
  {
    id: 'education',
    title: 'Education',
    description:
      'Translate study notes, academic diagrams, and scanned articles into 30+ languages with clarity. Layouts are preserved, so charts and diagrams remain easy to follow. Students and researchers gain broader perspectives, break learning barriers, and make education more inclusive by connecting with resources worldwide.',
    imageUrl:
      'https://assets.falcocut.ai/ai-image-translator/use-case-education-learning-materials-translation.webp',
  },
  {
    id: 'offices-teams',
    title: 'Offices & Teams',
    description:
      'Workplace collaboration improves when documents, charts, and presentations are translated quickly. FalcoCut extracts text from invoices, reports, and scanned papers, reducing manual data entry errors. Teams can share multilingual materials without confusion, boosting productivity and communication.',
    imageUrl:
      'https://assets.falcocut.ai/ai-image-translator/use-case-offices-teams-document-translation.webp',
  },
  {
    id: 'creators',
    title: 'Creators',
    description:
      'Reach global audiences with translated social media posts, memes, and image-based captions. All layouts and styles remain intact, so content feels authentic in every language. Influencers can connect with followers worldwide, encouraging more likes, shares, and comments.',
    imageUrl:
      'https://assets.falcocut.ai/ai-image-translator/use-case-creators-social-content-translation.webp',
  },
];

interface UseCaseTabProps {
  useCase: UseCase;
  isActive: boolean;
  onClick: () => void;
}

const UseCaseTab: React.FC<UseCaseTabProps> = ({ useCase, isActive, onClick }) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left rounded-2xl transition-colors duration-300 p-4 pad:p-5 ${
        isActive
          ? 'bg-[#6366F1] text-white'
          : 'bg-[#F5F5F7] text-gray-700 hover:bg-[#EBEBED]'
      }`}
    >
      <h3
        className={`font-semibold text-base pad:text-base ${isActive ? 'text-white' : 'text-gray-800'}`}
      >
        {useCase.title}
      </h3>
      <div
        className={`grid transition-all duration-300 ease-in-out ${
          isActive ? 'grid-rows-[1fr] opacity-100 mt-3' : 'grid-rows-[0fr] opacity-0'
        }`}
      >
        <div className="overflow-hidden">
          <p className="text-sm pad:text-base text-white/90 leading-relaxed">
            {useCase.description}
          </p>
        </div>
      </div>
    </button>
  );
};

const UseCasesSection: React.FC = () => {
  const [activeIndex, setActiveIndex] = useState(0);

  const activeUseCase = useCases[activeIndex];

  // 预加载所有图片
  const imageUrls = useMemo(() => useCases.map((uc) => uc.imageUrl), []);
  useImagePreload(imageUrls);

  const handleTabClick = (index: number) => {
    setActiveIndex(index);
  };

  return (
    <section className="w-full bg-white py-[60px] pad:py-[80px] sm:py-[100px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        {/* Title */}
        <h2 className="text-2xl pad:text-3xl sm:text-[42px] font-bold text-center text-gray-900 mb-4 pad:mb-5 leading-tight">
          Real-World Uses for AI Image Translation
        </h2>

        {/* Description */}
        <p className="text-sm pad:text-base sm:text-base text-center text-gray-600 max-w-5xl mx-auto mb-10 pad:mb-14 sm:mb-16 leading-relaxed">
          FalcoCut adapts to the needs of businesses, creators, and everyday users, making
          image translation simple and impactful across industries. By serving diverse
          audiences, it helps break language barriers, expand reach, and improve
          communication in real-world scenarios.
        </p>

        {/* Content */}
        <div className="flex flex-col pad:flex-row gap-6 pad:gap-8 sm:gap-12 items-stretch">
          {/* Left - Image */}
          <div className="w-full pad:w-[55%] flex items-center justify-center">
            <div className="relative w-full rounded-2xl overflow-hidden bg-gray-100 aspect-[4/3]">
              <Image
                src={activeUseCase.imageUrl}
                alt={activeUseCase.title}
                fill
                className="object-cover transition-opacity duration-300"
                sizes="(max-width: 768px) 100vw, 50vw"
              />
            </div>
          </div>

          {/* Right - Tabs */}
          <div className="w-full pad:w-[45%] flex flex-col gap-3 pad:gap-4">
            {useCases.map((useCase, index) => (
              <UseCaseTab
                key={useCase.id}
                useCase={useCase}
                isActive={index === activeIndex}
                onClick={() => handleTabClick(index)}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default UseCasesSection;
